import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const UserContext = createContext(null);

/**
 * UserProvider - Provides user profile and trial status to all child components
 *
 * This context makes it easy to check if user can create/edit data across the app.
 * When trial has expired and user is not premium, canCreateData will be false.
 */
export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derived state for easy access
  const canCreateData = user?.can_create_data ?? true;
  const isPremium = user?.is_premium ?? false;
  const isTrialActive = user?.is_trial_active ?? false;
  const trialEndsAt = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;

  const fetchUser = async () => {
    if (!api.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const profile = await api.getProfile();
      setUser(profile);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // Refresh user data (useful after upgrades)
  const refreshUser = () => {
    setLoading(true);
    fetchUser();
  };

  const value = {
    user,
    loading,
    canCreateData,
    isPremium,
    isTrialActive,
    trialEndsAt,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

/**
 * useUser hook - Access user context anywhere in the app
 *
 * Usage:
 * const { user, canCreateData, isPremium } = useUser();
 *
 * if (!canCreateData) {
 *   // Show disabled state or hide create buttons
 * }
 */
export function useUser() {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
